import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useSessionContext from '../../../data/queryHooks/useSessionContext';
import {
	findPermissionInPermissions,
	getRedirectionLink,
	getHomePagePath,
} from '../../../scripts/navigationHelpers';
import {
	retrievePermissionWithKey,
	retrievePageKeyWithRoute,
} from '../../../scripts/pageConstants';
import { HOME_URL } from '../../../scripts/navigationItems';
import { FITNESS_PAGE_ROUTES, TOP_LEVEL_ROUTES } from '../../../scripts/routes';
import { flowPaths } from '../../../scripts/flowPaths';

const ProtectedRoute = ({
	children,
	hasDefaultLocation,
	setHasDefaultLocation,
}) => {
	const {
		isLoggedIn,
		permissions,
		redirectionLinks,
		flows: incompleteFlows,
	} = useSessionContext();
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		// If the user doesn't have a default location set, they cannot go to any other page
		if (
			!hasDefaultLocation &&
			location.pathname !== TOP_LEVEL_ROUTES.confirmLocation
		) {
			navigate(TOP_LEVEL_ROUTES.confirmLocation, { replace: true });
		}
	}, [location.pathname, hasDefaultLocation]);

	// TODO
	// This logic here is temporary and will be replaced to original logic
	// once the page key is available for the identity page
	// This is just to test integration of identity react project
	// Original logic:
	// const currentPageKey = retrievePageKeyWithRoute[location.pathname.toLowerCase()];
	const currentPageKey = location.pathname.includes('/identity')
		? retrievePageKeyWithRoute['/identity']
		: retrievePageKeyWithRoute[location.pathname.toLowerCase()];

	const homeUrl = getHomePagePath(permissions, redirectionLinks) || HOME_URL;
	// use pageKey to get matching permission
	// find the permission from permissons,
	// if found, check to see if redirection link is available
	// if not found, redirect to login page

	if (permissions && currentPageKey) {
		let permissionFound = false;
		if (
			location.pathname.includes(
				FITNESS_PAGE_ROUTES.practitionerEducationLibrary
			)
		) {
			const pracEL = permissions.filter(perm =>
				perm.includes('PractitionerEducationLibrary')
			);
			permissionFound = pracEL.length > 0;
		} else if (
			location.pathname.includes(
				FITNESS_PAGE_ROUTES.providerEducationLibrary
			)
		) {
			const provEL = permissions.filter(perm =>
				perm.includes('ProviderEducationLibrary')
			);
			permissionFound = provEL.length > 0;
		} else {
			permissionFound = findPermissionInPermissions(
				retrievePermissionWithKey[currentPageKey],
				permissions
			);
		}

		if (!permissionFound) {
			window.open(homeUrl, '_self', 'noreferrer');
			return null;
		}

		// find the highest priority unskippable flow item
		const highestPriorityIncompleteFlow = incompleteFlows
			.filter(flows => !flows.canSkip)
			.reduce(
				(min, current) =>
					current.priority < min.priority ? current : min,
				incompleteFlows.length > 0 ? incompleteFlows[0] : null
			);

		// get url and for the first non-completed flow from flowPaths
		const firstIncompleteFlowPath = flowPaths.find(
			flowPath => flowPath?.type === highestPriorityIncompleteFlow?.type
		);

		if (
			location.pathname === '/' &&
			highestPriorityIncompleteFlow?.flow ===
				firstIncompleteFlowPath?.flow
		) {
			navigate(firstIncompleteFlowPath.url);
		} else {
			// redirect the user if the redirection link is available
			const link = getRedirectionLink(currentPageKey, redirectionLinks);

			if (link) {
				if (link.includes('.aspx')) {
					window.open(link, '_self', 'noreferrer');
					return null;
				}

				navigate(link);
				return null;
			}
		}
	}

	// redirect the user to the home page if no page key associated with the url
	if (!currentPageKey || !isLoggedIn) {
		window.open(homeUrl, '_self', 'noreferrer');
		return null;
	}

	const context = {
		hasDefaultLocation,
		setHasDefaultLocation,
	};

	return children || <Outlet context={context} />;
};

export default ProtectedRoute;

ProtectedRoute.propTypes = {
	children: PropTypes.element,
	hasDefaultLocation: PropTypes.bool.isRequired,
	setHasDefaultLocation: PropTypes.func.isRequired,
};

ProtectedRoute.defaultProps = {
	children: null,
};
